/* eslint-disable no-lone-blocks */
import Flatpickr from "react-flatpickr";
import AutoCompleteSelect from "../../Common/AutoCompleteSelect";
import useToggle from "../../../hooks/useToggle";
import useAutoComplete from "hooks/useAutoComplete";
import useApiClient from "hooks/useApiClient";
import { useFlightContext } from "hooks/flight/useFlightContext";
import { dateFormater, dayFromDate } from "helpers/common";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import MobileAutoComplete from "./MobileAutoComplete";
import useGetFlightAvailbility from "hooks/flight/useGetFlightAvailability";
import useFlightSearchParams from "hooks/flight/useFlightSearchParams";
import { useEffect, useRef, useState } from "react";
import MobilePassengersDropDown from "./MobilePassengersDropDown";
import { BiMinus, BiPlus } from "react-icons/bi";
import { FaPlaneDeparture } from "react-icons/fa6";
import { FaPlaneArrival } from "react-icons/fa";
import MultiDestinationTab from "./multiDestinationTab";
import { IoAdd } from "react-icons/io5";
import { French } from "flatpickr/dist/l10n/fr.js";
import { Arabic } from "flatpickr/dist/l10n/ar.js";
import { useIsMobile } from "../../../hooks/useIsMobile";

const mapFlightResponseToOptions = (data) => {
    return data.map((airport) => ({
        id: airport.id,
        city_name: airport.city_name,
        iata: airport.iata,
        country_name: airport.country_name,
        airport_name: airport.name
            ? airport.name
            : airport.city_name + " Airport",
    }));
};

const mapTopWeightedAirportsToOptions = (data) => {
    return data.map((airport) => ({
        id: airport?.id ?? airport?.iata,
        city_name: airport?.city_name ?? airport?.city ?? "",
        iata: airport?.iata ?? airport?.id ?? "",
        country_name: airport?.country_name ?? airport?.country ?? "",
        airport_name:
            airport?.name ??
            airport?.airport_name ??
            ((airport?.city_name ?? airport?.city ?? "") + " Airport"),
    }));
};

const FlightTab = () => {
    const { isOpen: departureIsOpen, toggle: departureToggle } = useToggle();
    const { isOpen: arrivalIsOpen, toggle: arrivalToggle } = useToggle();
    const {
        isOpen: passengersDropDownIsOpen,
        toggle: passengersDropDownToggle,
    } = useToggle();
    const { urls, api } = useApiClient();
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const flatpickrRef = useRef(null);
    const {
        term: departureTerm,
        results: departureResults,
        loading: departureLoading,
        handleInputChange: handleDepartureInputChange,
        setResults: setDepartureResults,
        setTerm: setDepartureTerm,
    } = useAutoComplete({
        url: urls.flight.airportAutocomplete,
        mapResponseToOptions: mapFlightResponseToOptions,
    });

    const {
        term: arrivalTerm,
        results: arrivalResults,
        loading: arrivalLoading,
        handleInputChange: handleArrivalInputChange,
        setResults: setArrivalResults,
        setTerm: setArrivalTerm,
    } = useAutoComplete({
        url: urls.flight.airportAutocomplete,
        mapResponseToOptions: mapFlightResponseToOptions,
    });

    const {
        updateFlightSearchParams,
        updateTravelers,
        getTravelersCount,
        isReadyToSearch,
    } = useFlightSearchParams();
    const { flightSearchParams, availabilityLoading } = useFlightContext();
    const navigate = useNavigate();
    const { getProviders } = useGetFlightAvailbility();
    const [animate, setAnimate] = useState(false);
    const [topAirportsCache, setTopAirportsCache] = useState(null);
    const { isMobile } = useIsMobile();
    const isTripTypeInitialMount = useRef(true);

    // Function to set up responsive navigation for datepickers
    const setupTwoMonthNavigation = (flatpickrInstance) => {
        if (!flatpickrInstance || !flatpickrInstance.calendarContainer) return;
        
        const calendar = flatpickrInstance.calendarContainer;
        const prevButton = calendar.querySelector('.flatpickr-prev-month');
        const nextButton = calendar.querySelector('.flatpickr-next-month');
        
        // Get the number of months shown from the calendar instance
        const showMonths = flatpickrInstance.config.showMonths || 1;
        const monthStep = showMonths; // Navigate by the number of months shown
        
        if (prevButton) {
            const newPrevButton = prevButton.cloneNode(true);
            prevButton.parentNode?.replaceChild(newPrevButton, prevButton);
            
            newPrevButton.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                const currentMonth = flatpickrInstance.currentMonth;
                const currentYear = flatpickrInstance.currentYear;
                let newMonth = currentMonth - monthStep;
                let newYear = currentYear;
                
                if (newMonth < 0) {
                    newMonth += 12;
                    newYear -= 1;
                }
                
                flatpickrInstance.changeMonth(newMonth, false);
                flatpickrInstance.changeYear(newYear);
                setTimeout(ensureWeekdaysPerMonth, 0);
            });
        }
        
        if (nextButton) {
            const newNextButton = nextButton.cloneNode(true);
            nextButton.parentNode?.replaceChild(newNextButton, nextButton);
            
            newNextButton.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                const currentMonth = flatpickrInstance.currentMonth;
                const currentYear = flatpickrInstance.currentYear;
                let newMonth = currentMonth + monthStep;
                let newYear = currentYear;
                
                if (newMonth > 11) {
                    newMonth -= 12;
                    newYear += 1;
                }
                
                flatpickrInstance.changeMonth(newMonth, false);
                flatpickrInstance.changeYear(newYear);
                setTimeout(ensureWeekdaysPerMonth, 0);
            });
        }
    };

    // Ensure each month in the 2‑months calendar has its own weekday header
    const ensureWeekdaysPerMonth = () => {
        const calendar = document.querySelector('.flatpickr-calendar.open')
        if (!calendar) return

        const monthContainers = calendar.querySelectorAll('.flatpickr-rContainer')
        if (!monthContainers.length) return

        const firstWeekdays = monthContainers[0].querySelector('.flatpickr-weekdays')
        if (!firstWeekdays) return

        monthContainers.forEach((container, index) => {
            if (index === 0) return
            const hasWeekdays = container.querySelector('.flatpickr-weekdays')
            const daysElement = container.querySelector('.flatpickr-days')

            if (!hasWeekdays && daysElement) {
                const cloned = firstWeekdays.cloneNode(true)
                container.insertBefore(cloned, daysElement)
            }
        })
    };

    useEffect(() => {
        setAnimate(true);
        return () => {
            setAnimate(false);
        };
    }, []);

    const search = () => {
        const goToCalendar =
            flightSearchParams?.flexible &&
            (flightSearchParams?.tripType === "ow" || flightSearchParams?.tripType === "rt");

        if (goToCalendar) {
            if (!location.pathname.includes("flight-calendar")) {
                navigate("/flight-calendar");
            }
            return;
        }

        if (!location.pathname.includes("flight-search")) {
            navigate("/flight-search");
        }
        setTimeout(() => {
            const el = document.getElementById(
                "flight_search_result_for_scrolling"
            );
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
        getProviders();
    };

    const getInputValue = (option) => {
        if (option) {
            return `${option.city_name} (${option.iata})`;
        }
        return "";
    };

    const stopSelection = (e) => e.preventDefault();

    const fetchTopWeightedAirports = async (limit = 6) => {
        if (topAirportsCache && topAirportsCache.length > 0) {
            return topAirportsCache;
        }
        try {
            const res = await api.get(urls.flight.topWeightedAirports, {
                params: {
                    limit,
                    language: i18n.language,
                },
            });
            const raw = Array.isArray(res?.data?.data)
                ? res.data.data
                : Array.isArray(res?.data)
                ? res.data
                : [];
            const options = mapTopWeightedAirportsToOptions(raw);
            setTopAirportsCache(options);
            return options;
        } catch (error) {
            console.error("Error fetching top weighted airports:", error);
            return [];
        }
    };

    const preloadDepartureTopAirports = async () => {
        if (departureTerm.trim().length > 0) return;
        const options = await fetchTopWeightedAirports();
        setDepartureResults(options);
    };

    const preloadArrivalTopAirports = async () => {
        if (arrivalTerm.trim().length > 0) return;
        const options = await fetchTopWeightedAirports();
        setArrivalResults(options);
    };


    // Reset dates when trip type changes (skip on initial mount to preserve dates after navigation)
    useEffect(() => {
        if (isTripTypeInitialMount.current) {
            isTripTypeInitialMount.current = false;
            return;
        }
        if (flatpickrRef.current && flatpickrRef.current.flatpickr) {
            flatpickrRef.current.flatpickr.set(
                "mode",
                flightSearchParams?.tripType === "rt" ? "range" : "single"
            );

            flatpickrRef.current.flatpickr.clear();
        }
    }, [flightSearchParams?.tripType]);

    // When openReturn toggled on: clear departureDate2 (mode switch is handled by the Flatpickr `key` remount)
    useEffect(() => {
        if (flightSearchParams?.openReturn) {
            updateFlightSearchParams("departureDate2", "");
        }
    }, [flightSearchParams?.openReturn, updateFlightSearchParams]);

    return (
        <div className={` ${animate ? "fade-in-tab" : ""}`}>
            <div className="row">
                <div className="col-lg-12">
                    <div className="flight_categories_search">
                        <ul className="nav nav-tabs">
                            <li className="nav-item">
                                <button
                                    onClick={() => {
                                        updateFlightSearchParams("tripType", "ow");
                                        updateFlightSearchParams("openReturn", false);
                                    }}
                                    className={`nav-link  ${
                                        flightSearchParams?.tripType === "ow" &&
                                        "s-link"
                                    }`}
                                    type="button"
                                >
                                    {t("oneWay")}
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    onClick={() => {
                                        updateFlightSearchParams(
                                            "tripType",
                                            "rt"
                                        );
                                    }}
                                    className={`nav-link ${
                                        flightSearchParams?.tripType === "rt" &&
                                        "s-link"
                                    }`}
                                    type="button"
                                >
                                    {t("roundTrip")}
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    onClick={() => {
                                        updateFlightSearchParams("tripType", "md");
                                        updateFlightSearchParams("openReturn", false);
                                    }}
                                    className={`nav-link ${
                                        flightSearchParams?.tripType === "md" &&
                                        "s-link"
                                    }`}
                                    type="button"
                                >
                                    {t("multiDestinations")}
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
            {flightSearchParams?.tripType === "md" ? (
                <MultiDestinationTab />
            ) : (
                <div className="tab-pane fade show ">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="oneway_search_form">
                                <form action="#!">
                                    <div className="row">
                                        <div className="col-lg-3 col-md-6 col-sm-12 col-12">
                                            <div className="flight_Search_boxed block md:hidden">
                                                <p>{t("whereFrom")}</p>
                                                <input
                                                    type="text"
                                                    value={getInputValue(
                                                        flightSearchParams?.departureOption1
                                                    )}
                                                    onClick={departureToggle}
                                                    placeholder={t(
                                                        "selectLocation"
                                                    )}
                                                />
                                                {flightSearchParams?.departureOption1 ? (
                                                    <span className="truncate text-ellipsis">
                                                        {
                                                            flightSearchParams
                                                                ?.departureOption1
                                                                ?.airport_name
                                                        }
                                                    </span>
                                                ) : (
                                                    <span className="text-transparent">
                                                        .
                                                    </span>
                                                )}
                                                <div className="plan_icon_posation">
                                                    <FaPlaneDeparture
                                                        size={22}
                                                    />
                                                </div>
                                            </div>
                                            <div className="flight_Search_boxed hidden md:block border-none ">
                                                <p>{t("whereFrom")}</p>
                                                <AutoCompleteSelect
                                                    name="flight"
                                                    options={departureResults}
                                                    value={departureTerm}
                                                    onInputChange={
                                                        handleDepartureInputChange
                                                    }
                                                    isLoading={departureLoading}
                                                    onMenuOpen={
                                                        preloadDepartureTopAirports
                                                    }
                                                    selectedOption={
                                                        flightSearchParams?.departureOption1
                                                    }
                                                    onChange={(
                                                        selectedOption
                                                    ) => {
                                                        updateFlightSearchParams(
                                                            "departureOption1",
                                                            selectedOption
                                                        );
                                                        if (!selectedOption) {
                                                            setDepartureTerm("");
                                                            fetchTopWeightedAirports().then(
                                                                (options) => {
                                                                    setDepartureResults(options);
                                                                }
                                                            );
                                                        }
                                                    }}
                                                />
                                                {flightSearchParams?.departureOption1 ? (
                                                    <span className="truncate text-ellipsis">
                                                        {
                                                            flightSearchParams
                                                                ?.departureOption1
                                                                ?.airport_name
                                                        }
                                                    </span>
                                                ) : (
                                                    <span className="text-transparent">
                                                        .
                                                    </span>
                                                )}
                                                <div className="plan_icon_posation">
                                                    <FaPlaneDeparture
                                                        size={22}
                                                    />
                                                </div>
                                            </div>
                                            <MobileAutoComplete
                                                type={"flight"}
                                                paramName="departureOption1"
                                                isOpen={departureIsOpen}
                                                onHide={departureToggle}
                                                placeholder={t("whereFrom")}
                                                id="dep1"
                                            />
                                        </div>
                                        <div className="col-lg-3 col-md-6 col-sm-12 col-12">
                                            <div className="flight_Search_boxed block md:hidden">
                                                <p>{t("whereTo")}</p>
                                                <input
                                                    type="text"
                                                    onClick={arrivalToggle}
                                                    value={getInputValue(
                                                        flightSearchParams?.arrivalOption1
                                                    )}
                                                    placeholder={t(
                                                        "selectLocation"
                                                    )}
                                                />
                                                {flightSearchParams?.arrivalOption1 ? (
                                                    <span className="truncate text-ellipsis">
                                                        {
                                                            flightSearchParams
                                                                ?.arrivalOption1
                                                                ?.airport_name
                                                        }
                                                    </span>
                                                ) : (
                                                    <span className="text-transparent">
                                                        .
                                                    </span>
                                                )}
                                                <div className="plan_icon_posation">
                                                    <FaPlaneArrival
                                                        size={22}
                                                        className="text-paragraph-color"
                                                    />
                                                </div>
                                            </div>
                                            <div className="flight_Search_boxed hidden md:block border-none ">
                                                <p>{t("whereTo")}</p>
                                                <AutoCompleteSelect
                                                    name={"flight"}
                                                    options={arrivalResults}
                                                    value={arrivalTerm}
                                                    onInputChange={
                                                        handleArrivalInputChange
                                                    }
                                                    isLoading={arrivalLoading}
                                                    onMenuOpen={
                                                        preloadArrivalTopAirports
                                                    }
                                                    selectedOption={
                                                        flightSearchParams?.arrivalOption1
                                                    }
                                                    onChange={(
                                                        selectedOption
                                                    ) => {
                                                        updateFlightSearchParams(
                                                            "arrivalOption1",
                                                            selectedOption
                                                        );
                                                        if (!selectedOption) {
                                                            setArrivalTerm("");
                                                            fetchTopWeightedAirports().then(
                                                                (options) => {
                                                                    setArrivalResults(options);
                                                                }
                                                            );
                                                        }
                                                    }}
                                                />
                                                {flightSearchParams?.arrivalOption1 ? (
                                                    <span className="truncate text-ellipsis">
                                                        {
                                                            flightSearchParams
                                                                ?.arrivalOption1
                                                                ?.airport_name
                                                        }
                                                    </span>
                                                ) : (
                                                    <span className="text-transparent">
                                                        .
                                                    </span>
                                                )}
                                                <div className="plan_icon_posation">
                                                    <FaPlaneArrival size={22} />
                                                </div>
                                            </div>
                                            <MobileAutoComplete
                                                type={"flight"}
                                                paramName={"arrivalOption1"}
                                                isOpen={arrivalIsOpen}
                                                onHide={arrivalToggle}
                                                placeholder={t("whereTo")}
                                                id="arr1"
                                            />
                                        </div>
                                        <div className="col-lg-4  col-md-6 col-sm-12 col-12">
                                            <div className="form_search_date h-full">
                                                <div className="flight_Search_boxed date_flex_area">
                                                    <div className="Journey_date w-full">
                                                        <p>
                                                            {flightSearchParams?.tripType === "rt" && !flightSearchParams?.openReturn
                                                                ? t("journeyDates")
                                                                : t("journeyDate")}
                                                        </p>
                                                        <div className="">
                                                            {flightSearchParams?.tripType ===
                                                            "ow" ? (
                                                                /* One-Way Date Picker */
                                                                <Flatpickr
                                                                    key={"ow"}
                                                                    value={
                                                                        flightSearchParams?.departureDate1 ||
                                                                        null
                                                                    }
                                                                    onChange={(
                                                                        dates
                                                                    ) => {
                                                                        updateFlightSearchParams(
                                                                            "departureDate1",
                                                                            dateFormater(
                                                                                dates[0]
                                                                            )
                                                                        );
                                                                    }}
                                                                    options={{
                                                                        mode: "single",
                                                                        altInput: true,
                                                                        altFormat:
                                                                            "F j Y",
                                                                        dateFormat:
                                                                            "Y-m-d",
                                                                        minDate:
                                                                            "today",
                                                                        showMonths: isMobile ? 1 : 2,
                                                                        animate: true,
                                                                        locale: i18n.language === "ar" ? Arabic : i18n.language === "fr" ? French : undefined,
                                                                        onReady: (_selectedDates, _dateStr, instance) => {
                                                                            setupTwoMonthNavigation(instance);
                                                                            setTimeout(ensureWeekdaysPerMonth, 0);
                                                                        },
                                                                        onOpen: () => {
                                                                            setTimeout(ensureWeekdaysPerMonth, 0);
                                                                        },
                                                                        onMonthChange: () => {
                                                                            setTimeout(ensureWeekdaysPerMonth, 0);
                                                                        },
                                                                        disable: [
                                                                            function (date) {
                                                                                const today = new Date();
                                                                                today.setHours(0, 0, 0, 0);
                                                                                return date < today;
                                                                            }
                                                                        ]
                                                                    }}
                                                                    placeholder={t(
                                                                        "selectDate"
                                                                    )}
                                                                    className="w-full bg-transparent"
                                                                />
                                                            ) : (
                                                                /* Round-Trip Date Picker — single when openReturn */
                                                                <Flatpickr
                                                                    key={flightSearchParams?.openReturn ? "rt-open" : "rt"}
                                                                    ref={flatpickrRef}
                                                                    onChange={(
                                                                        dates
                                                                    ) => {
                                                                        updateFlightSearchParams(
                                                                            "departureDate1",
                                                                            dateFormater(dates[0])
                                                                        );
                                                                        if (!flightSearchParams?.openReturn) {
                                                                            dates.length > 1
                                                                                ? updateFlightSearchParams("departureDate2", dateFormater(dates[1]))
                                                                                : updateFlightSearchParams("departureDate2", "");
                                                                        }
                                                                    }}
                                                                    options={{
                                                                        mode: flightSearchParams?.openReturn ? "single" : "range",
                                                                        altInput: true,
                                                                        altFormat:
                                                                            "F j Y",
                                                                        dateFormat:
                                                                            "Y-m-d",
                                                                        minDate:
                                                                            "today",
                                                                        showMonths: isMobile ? 1 : 2,
                                                                        animate: true,
                                                                        locale: i18n.language === "ar" ? Arabic : i18n.language === "fr" ? French : undefined,
                                                                        onReady: (_selectedDates, _dateStr, instance) => {
                                                                            setupTwoMonthNavigation(instance);
                                                                            setTimeout(ensureWeekdaysPerMonth, 0);
                                                                        },
                                                                        onOpen: () => {
                                                                            setTimeout(ensureWeekdaysPerMonth, 0);
                                                                        },
                                                                        onMonthChange: () => {
                                                                            setTimeout(ensureWeekdaysPerMonth, 0);
                                                                        },
                                                                        disable: [
                                                                            function (date) {
                                                                                const today = new Date();
                                                                                today.setHours(0, 0, 0, 0);
                                                                                return date < today;
                                                                            }
                                                                        ]
                                                                    }}
                                                                    placeholder={
                                                                        flightSearchParams?.openReturn
                                                                            ? t("selectDate")
                                                                            : t("selectDates")
                                                                    }
                                                                    className="w-full h-[36px]"
                                                                    value={
                                                                        flightSearchParams?.openReturn
                                                                            ? (flightSearchParams?.departureDate1 || null)
                                                                            : [
                                                                                flightSearchParams?.departureDate1,
                                                                                flightSearchParams?.departureDate2,
                                                                              ].filter(Boolean)
                                                                    }
                                                                />
                                                            )}
                                                        </div>
                                                        {flightSearchParams?.departureDate1 ? (
                                                            <span className="truncate text-ellipsis">
                                                                {flightSearchParams?.departureDate2
                                                                    ? `${dayFromDate(
                                                                          flightSearchParams?.departureDate1
                                                                      )}-${dayFromDate(
                                                                          flightSearchParams?.departureDate2
                                                                      )}`
                                                                    : `${dayFromDate(
                                                                          flightSearchParams?.departureDate1
                                                                      )}`}
                                                            </span>
                                                        ) : (
                                                            <span className="text-transparent">
                                                                .
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-lg-2  col-md-6 col-sm-12 col-12">
                                            <div className="flight_Search_boxed dropdown_passenger_area">
                                                <p>{t("passengerClass")}</p>
                                                <div className="dropdown hidden md:block">
                                                    <button
                                                        className="dropdown-toggle focus:outline-none final-count h-[36px] text-[1rem]"
                                                        data-toggle="dropdown"
                                                        type="button"
                                                        id="dropdownMenuButton1"
                                                        data-bs-toggle="dropdown"
                                                        aria-expanded="false"
                                                    >
                                                        {getTravelersCount() !==
                                                        1
                                                            ? `${getTravelersCount()} ${t(
                                                                  "passengers"
                                                              )}`
                                                            : `1 ${t(
                                                                  "passenger"
                                                              )}`}
                                                    </button>
                                                    <div
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                        }}
                                                        className=" fade-in dropdown-menu dropdown_passenger_info"
                                                        aria-labelledby="dropdownMenuButton1"
                                                    >
                                                        <div className="traveller-calulate-persons">
                                                            <div className="passengers">
                                                                <h6>
                                                                    {t(
                                                                        "passengers"
                                                                    )}
                                                                </h6>
                                                                <div className="passengers-types">
                                                                    <div className="passengers-type">
                                                                        <div className="text">
                                                                            <span className="count pcount">
                                                                                {
                                                                                    flightSearchParams?.nrOfAdult
                                                                                }
                                                                            </span>
                                                                            <div className="type-label">
                                                                                <p className="text-sm  text-black">
                                                                                    {t(
                                                                                        "adultsAndPlus"
                                                                                    )}
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                        <div className="button-set flex gap-1">
                                                                            <div
                                                                                className=" size-7 cursor-pointer flex items-center justify-center border-2 border-main-color text-main-color"
                                                                                onMouseDown={
                                                                                    stopSelection
                                                                                }
                                                                                onClick={() =>
                                                                                    updateTravelers(
                                                                                        "ADT",
                                                                                        "ADD"
                                                                                    )
                                                                                }
                                                                            >
                                                                                <BiPlus
                                                                                    size={
                                                                                        22
                                                                                    }
                                                                                />
                                                                            </div>
                                                                            <div
                                                                                className=" size-7 cursor-pointer flex items-center justify-center border-2 border-main-color text-main-color"
                                                                                onMouseDown={
                                                                                    stopSelection
                                                                                }
                                                                                onClick={() =>
                                                                                    updateTravelers(
                                                                                        "ADT",
                                                                                        "SUB "
                                                                                    )
                                                                                }
                                                                            >
                                                                                <BiMinus
                                                                                    size={
                                                                                        22
                                                                                    }
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="passengers-type">
                                                                        <div className="text">
                                                                            <span className="count ccount">
                                                                                {
                                                                                    flightSearchParams?.nrOfChild
                                                                                }
                                                                            </span>
                                                                            <div className="type-label">
                                                                                <p className="text-lg  text-black">
                                                                                    {t(
                                                                                        "childrenTwoAndMinus"
                                                                                    )}
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                        <div className="button-set flex gap-1">
                                                                            <div
                                                                                className=" size-7 cursor-pointer flex items-center justify-center border-2 border-main-color text-main-color"
                                                                                onClick={() =>
                                                                                    updateTravelers(
                                                                                        "CHD",
                                                                                        "ADD"
                                                                                    )
                                                                                }
                                                                                onMouseDown={
                                                                                    stopSelection
                                                                                }
                                                                            >
                                                                                <BiPlus
                                                                                    size={
                                                                                        22
                                                                                    }
                                                                                />
                                                                            </div>
                                                                            <div
                                                                                className=" size-7 cursor-pointer flex items-center justify-center border-2 border-main-color text-main-color"
                                                                                onClick={() =>
                                                                                    updateTravelers(
                                                                                        "CHD",
                                                                                        "SUB "
                                                                                    )
                                                                                }
                                                                                onMouseDown={
                                                                                    stopSelection
                                                                                }
                                                                            >
                                                                                <BiMinus
                                                                                    size={
                                                                                        22
                                                                                    }
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="passengers-type">
                                                                        <div className="text">
                                                                            <span className="count incount">
                                                                                {
                                                                                    flightSearchParams?.nrOfInfant
                                                                                }
                                                                            </span>
                                                                            <div className="type-label">
                                                                                <p className="text-lg  text-black">
                                                                                    {t(
                                                                                        "infantsAndMinus"
                                                                                    )}
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                        <div className="button-set flex gap-1">
                                                                            <div
                                                                                className=" size-7 cursor-pointer flex items-center justify-center border-2 border-main-color text-main-color"
                                                                                onClick={() =>
                                                                                    updateTravelers(
                                                                                        "INF",
                                                                                        "ADD"
                                                                                    )
                                                                                }
                                                                                onMouseDown={
                                                                                    stopSelection
                                                                                }
                                                                            >
                                                                                <BiPlus
                                                                                    size={
                                                                                        22
                                                                                    }
                                                                                />
                                                                            </div>
                                                                            <div
                                                                                className=" size-7 cursor-pointer flex items-center justify-center border-2 border-main-color text-main-color"
                                                                                onClick={() =>
                                                                                    updateTravelers(
                                                                                        "INF",
                                                                                        "SUB "
                                                                                    )
                                                                                }
                                                                                onMouseDown={
                                                                                    stopSelection
                                                                                }
                                                                            >
                                                                                <BiMinus
                                                                                    size={
                                                                                        22
                                                                                    }
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="passengers-type">
                                                                        <div className="text">
                                                                            <span className="count">
                                                                                {
                                                                                    flightSearchParams?.nrOfSenior
                                                                                }
                                                                            </span>
                                                                            <div className="type-label">
                                                                                <p className="text-sm  text-black">
                                                                                    {t("seniorPassenger") || "3ème âge"}
                                                                                </p>
                                                                                <span className="text-paragraph-color text-xs">60+</span>
                                                                            </div>
                                                                        </div>
                                                                        <div className="button-set flex gap-1">
                                                                            <div
                                                                                className=" size-7 cursor-pointer flex items-center justify-center border-2 border-main-color text-main-color"
                                                                                onClick={() =>
                                                                                    updateTravelers(
                                                                                        "senior",
                                                                                        "ADD"
                                                                                    )
                                                                                }
                                                                                onMouseDown={
                                                                                    stopSelection
                                                                                }
                                                                            >
                                                                                <BiPlus
                                                                                    size={
                                                                                        22
                                                                                    }
                                                                                />
                                                                            </div>
                                                                            <div
                                                                                className=" size-7 cursor-pointer flex items-center justify-center border-2 border-main-color text-main-color"
                                                                                onClick={() =>
                                                                                    updateTravelers(
                                                                                        "senior",
                                                                                        "SUB "
                                                                                    )
                                                                                }
                                                                                onMouseDown={
                                                                                    stopSelection
                                                                                }
                                                                            >
                                                                                <BiMinus
                                                                                    size={
                                                                                        22
                                                                                    }
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="cabin-selection">
                                                                <h6>
                                                                    {t(
                                                                        "cabinClass"
                                                                    )}
                                                                </h6>
                                                                <div className="cabin-list">
                                                                    <button
                                                                        type="button"
                                                                        className={`label-select-btn ${
                                                                            flightSearchParams?.cabinClass ===
                                                                            "Y"
                                                                                ? "active"
                                                                                : ""
                                                                        }`}
                                                                        onClick={() => {
                                                                            updateFlightSearchParams(
                                                                                "cabinClass",
                                                                                "Y"
                                                                            );
                                                                        }}
                                                                    >
                                                                        <span className="muiButton-label">
                                                                            {t(
                                                                                "economy"
                                                                            )}
                                                                        </span>
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className={`label-select-btn flex items-center duration-150 ${
                                                                            flightSearchParams?.cabinClass ===
                                                                            "C"
                                                                                ? "active"
                                                                                : ""
                                                                        }`}
                                                                        onClick={() => {
                                                                            updateFlightSearchParams(
                                                                                "cabinClass",
                                                                                "C"
                                                                            );
                                                                        }}
                                                                    >
                                                                        <span className="muiButton-label">
                                                                            {t(
                                                                                "business"
                                                                            )}
                                                                        </span>
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className={`label-select-btn ${
                                                                            flightSearchParams?.cabinClass ===
                                                                            "F"
                                                                                ? "active"
                                                                                : ""
                                                                        }`}
                                                                        onClick={() => {
                                                                            updateFlightSearchParams(
                                                                                "cabinClass",
                                                                                "F"
                                                                            );
                                                                        }}
                                                                    >
                                                                        <span className="MuiButton-label">
                                                                            {t(
                                                                                "firstClass"
                                                                            )}
                                                                        </span>
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div
                                                    className="block md:hidden "
                                                    onClick={
                                                        passengersDropDownToggle
                                                    }
                                                >
                                                    <button
                                                        className="dropdown-toggle  focus:outline-none final-count h-[36px] text-[1rem]"
                                                        type="button"
                                                    >
                                                        {getTravelersCount() !==
                                                        1
                                                            ? `${getTravelersCount()} ${t(
                                                                  "passengers"
                                                              )}`
                                                            : `1 ${t(
                                                                  "passenger"
                                                              )}`}
                                                    </button>
                                                    <MobilePassengersDropDown
                                                        isOpen={
                                                            passengersDropDownIsOpen
                                                        }
                                                        onHide={
                                                            passengersDropDownToggle
                                                        }
                                                    />
                                                </div>
                                                <span>
                                                    {flightSearchParams?.cabinClass ===
                                                    "Y"
                                                        ? "Economy"
                                                        : flightSearchParams?.cabinClass ===
                                                          "C"
                                                        ? "Business"
                                                        : "First class"}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <div className="flex justify-between items-center mt-3 flex-col md:flex-row gap-3">
                <div className="flex items-center gap-4 my-3 flex-wrap">
                    <div className=" gap-2 w-fit space-x-2">
                        <input
                            onChange={(e) => {
                                updateFlightSearchParams(
                                    "refundable",
                                    e.target.checked
                                );
                            }}
                            className="form-check-input checked:bg-main-color checked:border-main-color"
                            type="checkbox"
                            id="flexCheckDefault"
                        />
                        <label className="text-sm">{t("refundableFare")}</label>
                    </div>
                    <div className=" gap-2 w-fit space-x-2">
                        <input
                            onChange={(e) => {
                                updateFlightSearchParams(
                                    "withBaggage",
                                    e.target.checked
                                );
                            }}
                            className="form-check-input checked:bg-main-color checked:border-main-color "
                            type="checkbox"
                            id="flexCheckDefault"
                        />
                        <label className="text-sm">
                            {t("withBaggageOnly")}
                        </label>
                    </div>
                    <div className=" gap-2 w-fit space-x-2">
                        <input
                            onChange={(e) => {
                                updateFlightSearchParams(
                                    "directFlight",
                                    e.target.checked
                                );
                            }}
                            className="form-check-input checked:bg-main-color checked:border-main-color"
                            type="checkbox"
                            id="flexCheckDefault"
                        />
                        <label className="text-sm">{t("directFlight")} </label>
                    </div>
                    {(flightSearchParams?.tripType === "ow" || flightSearchParams?.tripType === "rt") && (
                        <div className=" gap-2 w-fit space-x-2">
                            <input
                                onChange={(e) => {
                                    updateFlightSearchParams(
                                        "flexible",
                                        e.target.checked
                                    );
                                }}
                                checked={!!flightSearchParams?.flexible}
                                className="form-check-input checked:bg-main-color checked:border-main-color"
                                type="checkbox"
                                id="flexibleDatesCheck"
                            />
                            <label className="text-sm" htmlFor="flexibleDatesCheck">{t("flexibleDates")}</label>
                        </div>
                    )}
                    {flightSearchParams?.tripType === "rt" && (
                        <div className=" gap-2 w-fit space-x-2">
                            <input
                                onChange={(e) => {
                                    updateFlightSearchParams(
                                        "openReturn",
                                        e.target.checked
                                    );
                                }}
                                checked={!!flightSearchParams?.openReturn}
                                className="form-check-input checked:bg-main-color checked:border-main-color"
                                type="checkbox"
                                id="openReturnCheck"
                            />
                            <label className="text-sm" htmlFor="openReturnCheck">{t("openReturn") || "Billet ouvert"}</label>
                        </div>
                    )}
                    {flightSearchParams?.tripType === "md" && (
                        <div className=" gap-2 w-fit space-x-2 ">
                            <div
                                className="flight_Search_boxed dropdown_passenger_area flex gap-2"
                                style={{ paddingLeft: "10px" }}
                            >
                                <div className="dropdown hidden md:block">
                                    <button
                                        className="dropdown-toggle focus:outline-none final-count  text-[1rem]"
                                        data-toggle="dropdown"
                                        type="button"
                                        id="dropdownMenuButton1"
                                        data-bs-toggle="dropdown"
                                        aria-expanded="false"
                                    >
                                        {getTravelersCount() !== 1
                                            ? `${getTravelersCount()} ${t(
                                                  "passengers"
                                              )}`
                                            : `1 ${t("passenger")}`}
                                    </button>
                                    <div
                                        onClick={(e) => {
                                            e.stopPropagation();
                                        }}
                                        className=" fade-in dropdown-menu dropdown_passenger_info select-none"
                                        aria-labelledby="dropdownMenuButton1"
                                    >
                                        <div className="traveller-calulate-persons">
                                            <div className="passengers">
                                                <h6>{t("passengers")}</h6>
                                                <div className="passengers-types">
                                                    <div className="passengers-type">
                                                        <div className="text">
                                                            <span className="count pcount">
                                                                {
                                                                    flightSearchParams?.nrOfAdult
                                                                }
                                                            </span>
                                                            <div className="type-label">
                                                                <p className="text-lg  text-black">
                                                                    {t(
                                                                        "adults"
                                                                    )}
                                                                </p>
                                                                <span className="text-paragraph-color text-sm">
                                                                    12+yrs
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="button-set flex gap-1">
                                                            <div
                                                                className=" size-7 cursor-pointer flex items-center justify-center border-2 border-main-color text-main-color"
                                                                onClick={() =>
                                                                    updateTravelers(
                                                                        "ADT",
                                                                        "ADD"
                                                                    )
                                                                }
                                                            >
                                                                <BiPlus
                                                                    size={22}
                                                                />
                                                            </div>
                                                            <div
                                                                className=" size-7 cursor-pointer flex items-center justify-center border-2 border-main-color text-main-color"
                                                                onClick={() =>
                                                                    updateTravelers(
                                                                        "ADT",
                                                                        "SUB "
                                                                    )
                                                                }
                                                            >
                                                                <BiMinus
                                                                    size={22}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="passengers-type">
                                                        <div className="text">
                                                            <span className="count ccount">
                                                                {
                                                                    flightSearchParams?.nrOfChild
                                                                }
                                                            </span>
                                                            <div className="type-label">
                                                                <p className="text-lg  text-black">
                                                                    {t(
                                                                        "children"
                                                                    )}
                                                                </p>
                                                                <span>
                                                                    2 - Less
                                                                    than 12yrs
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="button-set flex gap-1">
                                                            <div
                                                                className=" size-7 cursor-pointer flex items-center justify-center border-2 border-main-color text-main-color"
                                                                onClick={() =>
                                                                    updateTravelers(
                                                                        "CHD",
                                                                        "ADD"
                                                                    )
                                                                }
                                                            >
                                                                <BiPlus
                                                                    size={22}
                                                                />
                                                            </div>
                                                            <div
                                                                className=" size-7 cursor-pointer flex items-center justify-center border-2 border-main-color text-main-color"
                                                                onClick={() =>
                                                                    updateTravelers(
                                                                        "CHD",
                                                                        "SUB "
                                                                    )
                                                                }
                                                            >
                                                                <BiMinus
                                                                    size={22}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="passengers-type">
                                                        <div className="text">
                                                            <span className="count incount">
                                                                {
                                                                    flightSearchParams?.nrOfInfant
                                                                }
                                                            </span>
                                                            <div className="type-label">
                                                                <p className="text-lg  text-black">
                                                                    {t(
                                                                        "infants"
                                                                    )}
                                                                </p>
                                                                <span>
                                                                    Less than
                                                                    2yrs
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="button-set flex gap-1">
                                                            <div
                                                                className=" size-7 cursor-pointer flex items-center justify-center border-2 border-main-color text-main-color"
                                                                onClick={() =>
                                                                    updateTravelers(
                                                                        "INF",
                                                                        "ADD"
                                                                    )
                                                                }
                                                            >
                                                                <BiPlus
                                                                    size={22}
                                                                />
                                                            </div>
                                                            <div
                                                                className=" size-7 cursor-pointer flex items-center justify-center border-2 border-main-color text-main-color"
                                                                onClick={() =>
                                                                    updateTravelers(
                                                                        "INF",
                                                                        "SUB "
                                                                    )
                                                                }
                                                            >
                                                                <BiMinus
                                                                    size={22}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="cabin-selection select-none">
                                                <h6>{t("cabinClass")}</h6>
                                                <div className="cabin-list">
                                                    <button
                                                        type="button"
                                                        className={`label-select-btn ${
                                                            flightSearchParams?.cabinClass ===
                                                            "Y"
                                                                ? "active"
                                                                : ""
                                                        }`}
                                                        onClick={() => {
                                                            updateFlightSearchParams(
                                                                "cabinClass",
                                                                "Y"
                                                            );
                                                        }}
                                                    >
                                                        <span className="muiButton-label">
                                                            {t("economy")}
                                                        </span>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`label-select-btn flex items-center duration-150 ${
                                                            flightSearchParams?.cabinClass ===
                                                            "C"
                                                                ? "active"
                                                                : ""
                                                        }`}
                                                        onClick={() => {
                                                            updateFlightSearchParams(
                                                                "cabinClass",
                                                                "C"
                                                            );
                                                        }}
                                                    >
                                                        <span className="muiButton-label">
                                                            {t("business")}
                                                        </span>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`label-select-btn ${
                                                            flightSearchParams?.cabinClass ===
                                                            "F"
                                                                ? "active"
                                                                : ""
                                                        }`}
                                                        onClick={() => {
                                                            updateFlightSearchParams(
                                                                "cabinClass",
                                                                "F"
                                                            );
                                                        }}
                                                    >
                                                        <span className="MuiButton-label">
                                                            {t("firstClass")}
                                                        </span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    className="block md:hidden "
                                    onClick={passengersDropDownToggle}
                                >
                                    <button
                                        className="dropdown-toggle  focus:outline-none final-count  text-[1rem]"
                                        type="button"
                                    >
                                        {getTravelersCount() !== 1
                                            ? `${getTravelersCount()} ${t(
                                                  "passengers"
                                              )}`
                                            : `1 ${t("passenger")}`}
                                    </button>
                                    <MobilePassengersDropDown
                                        isOpen={passengersDropDownIsOpen}
                                        onHide={passengersDropDownToggle}
                                    />
                                </div>
                                <span>
                                    {flightSearchParams?.cabinClass === "Y"
                                        ? "Economy"
                                        : flightSearchParams?.cabinClass === "C"
                                        ? "Business"
                                        : "First class"}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
                <div className="flex gap-2">
                    {flightSearchParams?.tripType === "md" &&
                        flightSearchParams?.numberOfBounds < 3 && (
                            <button
                                onClick={() => {
                                    updateFlightSearchParams(
                                        "numberOfBounds",
                                        flightSearchParams?.numberOfBounds + 1
                                    );
                                }}
                                className="btn bg-secondary-color  border-secondary-color"
                            >
                                <IoAdd
                                    className="text-white font-bold"
                                    size={24}
                                />
                            </button>
                        )}
                    <button
                        onClick={search}
                        className={`btn btn_theme btn_md ${
                            (!isReadyToSearch() || availabilityLoading) &&
                            "cursor-not-allowed"
                        }`}
                        disabled={!isReadyToSearch() || availabilityLoading}
                    >
                        {t("search")}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FlightTab;
